import React, { useState, useEffect } from 'react';
import usersIcon from '../../assets/images/users_icon.png';
import search_icon from '../../assets/images/search_icon.png';
import eyeIcon from '../../assets/images/eyeIcon.png';
import brownfilter_icon from '../../assets/images/brownfilter_icon.png';
import OrderDetailsModal from "../OrderDetailsModal";
import AddSelfOrderModal from "../AddSelfOrderModal";
import { useLocation } from "react-router-dom";
import { GetAllOrder, UpdateOrderStatus } from "../../Services/OrderService";
import api from "../../config/axios";
import endpoints from "../../config/endpoints";


export default function Orders() {
  const [activeTab, setActiveTab] = useState('all');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showAddSelfOrder, setShowAddSelfOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [allMergedCount, setAllMergedCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  const location = useLocation();
  const orderIdFromState = location.state?.orderId ?? null;

  const userRole = localStorage.getItem("userRole");
  const vendorId = userRole === "vendor" ? localStorage.getItem("userId") : null;

  // Date Filtering State
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeStartDate, setActiveStartDate] = useState('');
  const [activeEndDate, setActiveEndDate] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [tableTabCount, settableTabCount] = useState({
    allOrdersCount: 0,
    websiteOrdersCount: 0,
    whatsAppOrdersCount: 0,
    newOrdersCount: 0,
    inProgressCount: 0,
    outForDeliveryCount: 0,
    deliveredCount: 0,
  });

  // Calculate Dates for Filtering
  const getDatesForFilter = (filter, customStart, customEnd) => {
    const today = new Date();
    let start = '';
    let end = '';

    if (filter === 'today') {
      start = today.toLocaleDateString('sv-SE');
      end = today.toLocaleDateString('sv-SE');
    } else if (filter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      start = yesterday.toLocaleDateString('sv-SE');
      end = yesterday.toLocaleDateString('sv-SE');
    } else if (filter === 'thisWeek') {
      const currentDay = today.getDay(); // 0 is Sunday, 1 is Monday
      const distance = currentDay === 0 ? -6 : 1 - currentDay;
      const monday = new Date();
      monday.setDate(today.getDate() + distance);
      start = monday.toLocaleDateString('sv-SE');
      end = today.toLocaleDateString('sv-SE');
    } else if (filter === 'custom') {
      start = customStart;
      end = customEnd;
    }
    return { start, end };
  };

  useEffect(() => {
    const { start, end } = getDatesForFilter(dateFilter, customStartDate, customEndDate);
    setActiveStartDate(start);
    setActiveEndDate(end);
  }, [dateFilter, customStartDate, customEndDate]);

  // Helper: parse sheet date to "YYYY-MM-DD"
  const parseSheetDate = (dateStr) => {
    if (!dateStr) return null;
    const s = dateStr.toString().trim();
    const datePart = s.length >= 10 ? s.substring(0, 10) : s;
    const parts = datePart.split(/[-/]/);
    if (parts.length !== 3) return null;
    if (parts[0].length === 4) return datePart; // YYYY-MM-DD
    return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
  };

  // Helper: map Google Sheet row to Order object
  const mapSheetOrderToOrder = (row) => {
    const grainAddons = row.addOnGrains ? row.addOnGrains.split('|').map(s => s.trim()).filter(Boolean) : [];
    const grainQtys = row.addOnGrainsQty ? row.addOnGrainsQty.split('|').map(s => s.trim()) : [];
    const pulseAddons = row.addOnPulses ? row.addOnPulses.split('|').map(s => s.trim()).filter(Boolean) : [];
    const pulseQtys = row.addOnPulsesQty ? row.addOnPulsesQty.split('|').map(s => s.trim()) : [];

    const subProducts = [
      ...grainAddons.map((g, i) => ({ name: g, weight: grainQtys[i] || '' })),
      ...pulseAddons.map((p, i) => ({ name: p, weight: pulseQtys[i] || '' }))
    ];

    const attributes = [];
    if (row.grading) {
      attributes.push({ name: 'Grinding Texture', value: row.grading });
    }
    if (row.packaging) {
      attributes.push({ name: 'Packaging', value: row.packaging });
    }

    const parsedTotal = Number((row.total || '').toString().replace(/[^0-9.]/g, '')) || 0;
    const parsedWeight = Number((row.quantity || '').toString().replace(/[^0-9.]/g, '')) || 0;

    let createdOnUtc = new Date().toISOString();
    if (row.date) {
      const s = row.date.toString().trim();
      const parts = s.split(' ');
      const dateStr = parts[0];
      const timeStr = parts[1] || '00:00:00';
      const dParts = dateStr.split(/[-/]/);

      if (dParts.length === 3) {
        let year, month, day;
        if (dParts[0].length === 4) {
          year = dParts[0];
          month = dParts[1].padStart(2, '0');
          day = dParts[2].padStart(2, '0');
        } else if (dParts[2].length === 4) {
          day = dParts[0].padStart(2, '0');
          month = dParts[1].padStart(2, '0');
          year = dParts[2];
        }
        if (year && month && day) {
          const parsed = new Date(`${year}-${month}-${day}T${timeStr}`);
          if (!isNaN(parsed.getTime())) {
            createdOnUtc = parsed.toISOString();
          }
        }
      }
    }

    return {
      orderId: row.orderId || 'N/A',
      userName: row.customerName || 'N/A',
      userPhone: row.phone || '',
      shippingAddress: row.address || '',
      createdOnUtc: createdOnUtc,
      deliveryDate: '',
      weight: parsedWeight,
      totalPrice: parsedTotal,
      paymentStatus: row.status || 'Pending',
      orderStatus: row.status || 'Pending',
      paymentMethod: 'WhatsApp',
      products: [
        {
          productName: row.product || 'N/A',
          price: parsedTotal,
          weight: parsedWeight,
          attributes: {
            product: [{ name: row.product || 'N/A', weight: row.quantity || '' }],
            subProducts: subProducts,
            attributes: attributes
          }
        }
      ]
    };
  };

  // Helper to match Google Sheet row status to dashboard tabs
  const matchSheetStatusToTab = (sheetStatus, tabStatus) => {
    if (!sheetStatus) {
      return tabStatus === 'new';
    }
    const s = sheetStatus.toString().toLowerCase().trim();
    if (tabStatus === 'new') {
      return s === 'pending' || s === 'new' || s === '1';
    }
    if (tabStatus === 'inProgress') {
      return s === 'processing' || s === 'inprogress' || s === 'in progress' || s === '2';
    }
    if (tabStatus === 'outForDelivery') {
      return s === 'outfordelivery' || s === 'out for delivery' || s === '3';
    }
    if (tabStatus === 'delivered') {
      return s === 'delivered' || s === 'completed' || s === '4';
    }
    return false;
  };

  const loadSingleOrder = async (orderId) => {
    const res = await GetAllOrder({ orderId });
    if (res?.status && res.data.orders.length > 0) {
      const order = res.data.orders[0];
      setSelectedOrder(order);
      setShowOrderModal(true);
    }
  };

  const loadOrders = async (status, term = "") => {
    try {
      setIsLoadingOrders(true);
      setCurrentOrders([]);

      // Prepare DB params for orders matching date filter & vendor
      const dbParams = { pageIndex: 0, pageSize: 1000 };
      if (activeStartDate) dbParams.startDate = activeStartDate;
      if (activeEndDate) dbParams.endDate = activeEndDate;
      if (vendorId) dbParams.vendorId = vendorId;

      // Use allSettled so if WhatsApp sheet API fails, DB orders still show
      const [dbResult, sheetResult] = await Promise.allSettled([
        GetAllOrder(dbParams),
        api.get(`${endpoints.WhatsApp.GetSheetOrders}?pageSize=1000`)
      ]);

      const dbRes = dbResult.status === 'fulfilled' ? dbResult.value : null;
      const sheetRes = sheetResult.status === 'fulfilled' ? sheetResult.value : null;

      if (dbRes?.status) {
        // 1. All DB orders from SQL Database (filtering out orphan 0-price stubs with no products)
        const rawDbOrders = dbRes.data.orders || [];
        const allDbOrders = rawDbOrders.filter(o => (o.products && o.products.length > 0) || (o.totalPrice && o.totalPrice > 0));

        // 2. Google Sheet WhatsApp orders (mapped & date-filtered) — fallback to [] if sheet fails
        const rawSheetOrders = (sheetRes?.data?.status && sheetRes?.data?.data) ? sheetRes.data.data : [];
        let sheetOrders = rawSheetOrders.map(row => mapSheetOrderToOrder(row));

        if (activeStartDate && activeEndDate) {
          const start = new Date(activeStartDate);
          const end = new Date(activeEndDate);
          end.setHours(23, 59, 59, 999);
          sheetOrders = sheetOrders.filter(o => {
            const d = new Date(o.createdOnUtc);
            return d >= start && d <= end;
          });
        }

        // Helper to match DB order status
        const matchDbStatus = (orderStatus, tab) => {
          const s = (orderStatus || '').toLowerCase();
          if (tab === 'new') return s === 'pending' || s === 'new';
          if (tab === 'inProgress') return s === 'processing' || s === 'in progress' || s === 'inprogress';
          if (tab === 'outForDelivery') return s === 'outfordelivery' || s === 'out for delivery';
          if (tab === 'delivered') return s === 'completed' || s === 'delivered';
          return false;
        };

        // Helper to combine DB & Sheet orders with deduplication
        const combineDedupe = (dbArr, sheetArr) => {
          const map = new Map();
          dbArr.forEach(o => map.set(String(o.orderId || o.id), o));
          sheetArr.forEach(o => {
            const key = String(o.orderId || o.id);
            if (!map.has(key)) {
              map.set(key, o);
            }
          });
          return Array.from(map.values());
        };

        // Categorize DB & Sheet orders: Website = DB website orders, WhatsApp = Google Sheet orders
        const websiteOrdersCombined = allDbOrders.filter(o => 
          (o.orderSource || '').toLowerCase() === 'website' || 
          (o.orderSource || '').toLowerCase() === 'app' || 
          ((o.orderSource || '').toLowerCase() !== 'whatsapp' && (o.paymentMethod || '').toLowerCase() !== 'whatsapp')
        );
        const dbWhatsAppOrders = allDbOrders.filter(o => 
          (o.orderSource || '').toLowerCase() === 'whatsapp' || 
          (o.paymentMethod || '').toLowerCase() === 'whatsapp'
        );
        // If Google Sheet orders exist, use Sheet orders for WhatsApp tab (183 orders); fallback to DB WhatsApp orders if sheet fails
        const whatsAppOrdersCombined = sheetOrders.length > 0 ? sheetOrders : dbWhatsAppOrders;
        const allOrdersCombined = combineDedupe(websiteOrdersCombined, whatsAppOrdersCombined);
        const pendingOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'new')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'new'))
        );
        const inProgressOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'inProgress')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'inProgress'))
        );
        const outForDeliveryOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'outForDelivery')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'outForDelivery'))
        );
        const deliveredOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'delivered')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'delivered'))
        );

        // 3. Compute accurate counts for ALL tab badges
        settableTabCount({
          allOrdersCount: allOrdersCombined.length,
          websiteOrdersCount: websiteOrdersCombined.length,
          whatsAppOrdersCount: whatsAppOrdersCombined.length,
          newOrdersCount: pendingOrders.length,
          inProgressCount: inProgressOrders.length,
          outForDeliveryCount: outForDeliveryOrders.length,
          deliveredCount: deliveredOrders.length,
        });

        // 4. Select merged orders for current active tab
        let targetOrders = [];
        if (status === 'all') {
          targetOrders = allOrdersCombined;
        } else if (status === 'website') {
          targetOrders = websiteOrdersCombined;
        } else if (status === 'whatsapp') {
          targetOrders = whatsAppOrdersCombined;
        } else if (status === 'new') {
          targetOrders = pendingOrders;
        } else if (status === 'inProgress') {
          targetOrders = inProgressOrders;
        } else if (status === 'outForDelivery') {
          targetOrders = outForDeliveryOrders;
        } else if (status === 'delivered') {
          targetOrders = deliveredOrders;
        }

        // Sort merged orders by date descending
        targetOrders.sort((a, b) => new Date(b.createdOnUtc) - new Date(a.createdOnUtc));

        // 5. Apply search term filter if present
        let filtered = targetOrders;
        if (term) {
          const termLc = term.toLowerCase();
          filtered = targetOrders.filter(o => 
            (o.userName || '').toLowerCase().includes(termLc) || 
            (o.userPhone || '').toLowerCase().includes(termLc) ||
            (o.shippingAddress || '').toLowerCase().includes(termLc) ||
            (o.products || []).some(p => (p.productName || '').toLowerCase().includes(termLc))
          );
        }

        setAllMergedCount(filtered.length);
        setCurrentOrders(filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize));
      }
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (orderIdFromState) {
      loadSingleOrder(orderIdFromState);
    }
  }, [orderIdFromState]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadOrders(activeTab, searchTerm);
    }, 400);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeTab, activeStartDate, activeEndDate, currentPage, pageSize, vendorId]);

  const handleTabChange = (tab) => {
    setCurrentPage(0);
    setActiveTab(tab);
  };

  const handleDateFilterChange = (filter) => {
    setCurrentPage(0);
    setDateFilter(filter);
  };

  const getEndDateLimits = () => {
    const todayStr = new Date().toLocaleDateString('sv-SE');
    if (!customStartDate) return { max: todayStr };
    return {
      min: customStartDate,
      max: todayStr
    };
  };

  const getStartDateLimits = () => {
    const todayStr = new Date().toLocaleDateString('sv-SE');
    if (!customEndDate) return { max: todayStr };
    return {
      max: customEndDate
    };
  };

  const handleCustomDateChange = (type, val) => {
    setCurrentPage(0);
    if (type === 'start') {
      setCustomStartDate(val);
      if (customEndDate && val > customEndDate) {
        setCustomEndDate(val);
      }
    } else {
      setCustomEndDate(val);
      if (customStartDate && val < customStartDate) {
        setCustomStartDate(val);
      }
    }
  };

  const handleChangeOrderStatus = async (orderId, statusId, tab) => {
    const res = await UpdateOrderStatus(orderId, statusId);
    if (res?.status) {
      setShowOrderModal(false);
      loadOrders(activeTab, searchTerm);
    } else {
      console.error(`Failed to update status for order ID: ${orderId}`);
    }
    return res;
  };

  const moveToNextStep = async (order, customStatusId = null) => {
    if (customStatusId) {
      return handleChangeOrderStatus(order.orderId, customStatusId, activeTab);
    }

    let tab = "";
    let statusId = 0;
    const status = (order.orderStatus || '').toLowerCase();

    if (status === 'pending' || status === 'new') {
      tab = 'inProgress'; statusId = 2;
    } else if (status === 'processing' || status === 'inprogress') {
      tab = 'outForDelivery'; statusId = 3;
    } else if (status === 'outfordelivery') {
      tab = 'delivered'; statusId = 4;
    }

    return handleChangeOrderStatus(order.orderId, statusId, tab);
  };

  const totalOrders = allMergedCount;
  const totalPages = Math.ceil(totalOrders / pageSize);

  const getBtnStyle = (filterName) => ({
    backgroundColor: dateFilter === filterName ? "#5d4037" : "transparent",
    color: dateFilter === filterName ? "#ffffff" : "#5d4037",
    borderColor: "#5d4037",
    borderRadius: "20px",
    padding: "4px 14px",
    fontSize: "12px",
    fontWeight: "600",
    transition: "all 0.2s ease-in-out"
  });

  return (
    <div className="dashboard mt-5">
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div>
          <h2>Order List</h2>
          <p><small>Manage all website and WhatsApp orders here.</small></p>
        </div>
        <button
          className="add-btn btn d-flex align-items-center gap-2 px-4 py-2"
          onClick={() => setShowAddSelfOrder(true)}
          style={{ height: "45px", borderRadius: "8px", fontWeight: "bold" }}
        >
          <span className="fs-5 fw-bold">+</span> CREATE CALL ORDER
        </button>
      </div>

      {/* --- Date Presets & Custom Filter Bar --- */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3" style={{ background: "#ffffff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <span style={{ fontSize: "13px", fontWeight: "700", color: "#3e2723", marginRight: "6px" }}>Filter by Date:</span>
        <button 
          style={getBtnStyle('all')}
          onClick={() => handleDateFilterChange('all')}
        >
          All Time
        </button>
        <button 
          style={getBtnStyle('today')}
          onClick={() => handleDateFilterChange('today')}
        >
          Today
        </button>
        <button 
          style={getBtnStyle('yesterday')}
          onClick={() => handleDateFilterChange('yesterday')}
        >
          Yesterday
        </button>
        <button 
          style={getBtnStyle('thisWeek')}
          onClick={() => handleDateFilterChange('thisWeek')}
        >
          This Week
        </button>
        <button 
          style={getBtnStyle('custom')}
          onClick={() => handleDateFilterChange('custom')}
        >
          Custom Date Range
        </button>

        {dateFilter === 'custom' && (
          <div className="d-flex align-items-center gap-2 ms-md-auto">
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={customStartDate} 
              onChange={(e) => handleCustomDateChange('start', e.target.value)} 
              min={getStartDateLimits().min}
              max={getStartDateLimits().max}
              style={{ width: "150px" }}
            />
            <span style={{ fontSize: "13px", color: "#795548", fontWeight: "600" }}>to</span>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={customEndDate} 
              onChange={(e) => handleCustomDateChange('end', e.target.value)} 
              min={getEndDateLimits().min}
              max={getEndDateLimits().max}
              style={{ width: "150px" }}
            />
          </div>
        )}
      </div>

      {/* --- Order List & Tabs --- */}
      <div className="order-list">
        <div className="order-header">
          {/* --- TABS --- */}
          <div className="tabs">
            <button className={activeTab === 'all' ? 'active' : ''} onClick={() => handleTabChange('all')}>
              All ({tableTabCount.allOrdersCount || 0})
            </button>
            <button className={activeTab === 'website' ? 'active' : ''} onClick={() => handleTabChange('website')}>
              Website ({tableTabCount.websiteOrdersCount || 0})
            </button>
            <button className={activeTab === 'whatsapp' ? 'active' : ''} onClick={() => handleTabChange('whatsapp')}>
              WhatsApp ({tableTabCount.whatsAppOrdersCount || 0})
            </button>
            <button className={activeTab === 'new' ? 'active' : ''} onClick={() => handleTabChange('new')}>
              Pending ({tableTabCount.newOrdersCount || 0})
            </button>
            <button className={activeTab === 'inProgress' ? 'active' : ''} onClick={() => handleTabChange('inProgress')}>
              In Progress ({tableTabCount.inProgressCount || 0})
            </button>
            <button className={activeTab === 'outForDelivery' ? 'active' : ''} onClick={() => handleTabChange('outForDelivery')}>
              Out For Delivery ({tableTabCount.outForDeliveryCount || 0})
            </button>
            <button className={activeTab === 'delivered' ? 'active' : ''} onClick={() => handleTabChange('delivered')}>
              Delivered ({tableTabCount.deliveredCount || 0})
            </button>
          </div>

          {/* --- Search --- */}
          <div className="search-tools">
            <div className="search-wrapper">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Order"
              />
              <div className="icons">
                <img src={search_icon} alt="Search" className="search-icon" />
                <img src={brownfilter_icon} alt="Filter" className="filter-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* --- Order Table --- */}
        <div className="order-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Customer Details</th>
                <th>Order Items</th>
                <th>Delivery Location</th>
                <th>Placed On</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingOrders ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading orders...
                  </td>
                </tr>
              ) : currentOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-orders text-center">No orders found.</td>
                </tr>
              ) : (
                currentOrders.map((order, idx) => {
                  const statusLc = (order.orderStatus || '').toLowerCase();
                  const statusBg = statusLc === 'completed' || statusLc === 'delivered' || statusLc === 'paid' ? '#e8f5e9'
                    : statusLc === 'pending' || statusLc === 'new' ? '#fff3e0'
                    : statusLc === 'processing' || statusLc === 'in progress' || statusLc === 'inprogress' ? '#e3f2fd' : '#fafafa';
                  const statusColor = statusLc === 'completed' || statusLc === 'delivered' || statusLc === 'paid' ? '#2e7d32'
                    : statusLc === 'pending' || statusLc === 'new' ? '#ef6c00'
                    : statusLc === 'processing' || statusLc === 'in progress' || statusLc === 'inprogress' ? '#1565c0' : '#616161';
                  
                  const whatsAppLink = `https://wa.me/${(order.userPhone || '').replace(/[^0-9]/g, '')}`;
                  const googleMapsLink = order.shippingAddress && (order.shippingAddress.startsWith('http') || order.shippingAddress.includes('maps?q=')) 
                    ? order.shippingAddress 
                    : '';
                  const isAddressUrl = !!googleMapsLink;

                  return (
                    <tr key={order.orderId || idx}>
                      {/* Customer */}
                      <td>
                        <div className="customer-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={usersIcon} alt={order.userName} className="customer-img" />
                            <span style={{ fontWeight: '600' }}>{order.userName || 'N/A'}</span>
                          </div>
                          {order.userPhone && (
                            <a
                              href={whatsAppLink}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: '12px', color: '#25D366', marginLeft: '32px', textDecoration: 'none' }}
                            >
                              {order.userPhone}
                            </a>
                          )}
                        </div>
                      </td>
                      {/* Order Items */}
                      <td>
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          {order.products?.map((p, pIdx) => {
                            const productName = p.productName || 'N/A';
                            const wheatVariant = p.attributes?.product?.[0]?.name;
                            const wheatWeight = p.attributes?.product?.[0]?.weight;
                            const attrs = (p.attributes?.attributes || []).map(a => a.value).filter(Boolean);
                            const addons = (p.attributes?.subProducts || []);
                            const totalWeight = p.weight || p.quantity || 0;
                            return (
                              <div key={pIdx} style={{ marginBottom: pIdx < order.products.length - 1 ? '8px' : 0 }}>
                                <div style={{ fontWeight: '600', color: '#3e2723' }}>{productName}</div>
                                {wheatVariant && wheatVariant !== productName && (
                                  <div style={{ color: '#795548', fontSize: '11px' }}>
                                    Wheat: {wheatVariant}{wheatWeight ? ` (${wheatWeight})` : ''}
                                  </div>
                                )}
                                {attrs.length > 0 && (
                                  <div style={{ color: '#8d6e63', fontSize: '11px' }}>{attrs.join(' · ')}</div>
                                )}
                                {addons.length > 0 && (
                                  <div style={{ color: '#666', fontSize: '11px' }}>+ {addons.map(sp => `${sp.name} ${sp.weight || ''}`.trim()).join(', ')}</div>
                                )}
                                <div style={{ color: '#888', fontSize: '11px', marginTop: '2px' }}>{totalWeight} Kg · ₹{p.price}</div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      {/* Location */}
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          {order.shippingAddress && !isAddressUrl ? (
                            <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{order.shippingAddress}</span>
                          ) : null}
                          {isAddressUrl ? (
                            <a
                              href={order.shippingAddress}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#1976D2', fontWeight: '600', textDecoration: 'none', display: 'block' }}
                            >
                              📍 View Location
                            </a>
                          ) : !order.shippingAddress && order.userPhone ? (
                            <a
                              href={whatsAppLink}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#25D366', fontWeight: '600', textDecoration: 'none' }}
                            >
                              📱 {order.userPhone}
                            </a>
                          ) : null}
                        </div>
                      </td>
                      {/* Date */}
                      <td style={{ fontSize: '12px' }}>{new Date(order.createdOnUtc).toLocaleString()}</td>
                      {/* Amount */}
                      <td style={{ fontWeight: '600', color: '#3e2723' }}>
                        ₹ {Number(order.totalPrice).toLocaleString('en-IN')}
                      </td>
                      {/* Status */}
                      <td>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'inline-block', backgroundColor: statusBg, color: statusColor }}>
                          {order.orderStatus || 'Pending'}
                        </span>
                      </td>
                      {/* Action */}
                      <td className="action-icon">
                        <img
                          src={eyeIcon}
                          alt="View Details"
                          title="View Details"
                          className="eye-icon"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* --- Pagination Controls --- */}
        <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2" style={{ borderTop: "1px solid #efebe9" }}>
          <small style={{ color: "#795548", fontWeight: "500" }}>
            Showing {totalOrders > 0 ? currentPage * pageSize + 1 : 0} to {Math.min((currentPage + 1) * pageSize, totalOrders)} of {totalOrders} orders
          </small>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              style={{
                color: currentPage === 0 ? "#aaa" : "#5d4037",
                borderColor: currentPage === 0 ? "#ddd" : "#5d4037",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: currentPage === 0 ? "not-allowed" : "pointer"
              }}
            >
              Previous
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{
                color: currentPage >= totalPages - 1 ? "#aaa" : "#5d4037",
                borderColor: currentPage >= totalPages - 1 ? "#ddd" : "#5d4037",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: currentPage >= totalPages - 1 ? "not-allowed" : "pointer"
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <OrderDetailsModal
        show={showOrderModal}
        handleClose={() => setShowOrderModal(false)}
        order={selectedOrder}
        moveToNextStep={moveToNextStep}
      />

      <AddSelfOrderModal
        show={showAddSelfOrder}
        handleClose={() => setShowAddSelfOrder(false)}
        onSuccess={() => loadOrders(activeTab, searchTerm)}
      />
    </div>
  );
}
